import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import Logo from '../components/Logo';

export default function Register() {
    const nav = useNavigate();
    const [form, setForm] = useState({
        person_name: '', business_name: '', proprietor_name: '', mobile: '',
        address_line: '', city: '', state: '', pin_code: '',
        gstin: '', pan: '', email: '',
    });
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

    const submit = async () => {
        setError('');
        const required = ['person_name', 'business_name', 'proprietor_name', 'mobile', 'address_line', 'city', 'state', 'pin_code'];
        for (const r of required) {
            if (!form[r].trim()) { setError('Please fill all required fields'); return; }
        }
        if (!/^\d{10}$/.test(form.mobile.trim())) { setError('Enter a valid 10-digit mobile number'); return; }
        if (!/^\d{6}$/.test(form.pin_code.trim())) { setError('Enter a valid 6-digit PIN code'); return; }
        setBusy(true);
        try {
            await api.post('/dist/auth/register', form);
            nav('/pending-approval', { replace: true, state: { mobile: form.mobile } });
        } catch (e) {
            setError(e.response?.data?.detail || 'Registration failed');
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen bg-sdf-ivory">
            <div className="px-4 py-5 flex items-center justify-center border-b border-sdf-line">
                <Logo variant="login" />
            </div>
            <main className="max-w-2xl mx-auto px-4 py-8">
                <h1 className="text-2xl font-extrabold text-sdf-text">Register as a New Distributor</h1>
                <p className="mt-1 text-sm text-sdf-text-2">Your registration will be reviewed by our admin team. You will be able to log in once approved.</p>

                <div className="mt-6 card-soft p-5 space-y-4">
                    <Field label="Person Name" required value={form.person_name} onChange={(v) => update('person_name', v)} tid="reg-person-name" />
                    <Field label="Business Name" required value={form.business_name} onChange={(v) => update('business_name', v)} tid="reg-business-name" />
                    <Field label="Proprietor Name" required value={form.proprietor_name} onChange={(v) => update('proprietor_name', v)} tid="reg-proprietor" />
                    <Field label="Mobile Number" required prefix="+91" maxLength={10} value={form.mobile} onChange={(v) => update('mobile', v.replace(/\D/g, ''))} tid="reg-mobile" />
                    <Field label="Complete Address" required textarea value={form.address_line} onChange={(v) => update('address_line', v)} tid="reg-address" />
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="City" required value={form.city} onChange={(v) => update('city', v)} tid="reg-city" />
                        <Field label="State" required value={form.state} onChange={(v) => update('state', v)} tid="reg-state" />
                    </div>
                    <Field label="PIN Code" required maxLength={6} value={form.pin_code} onChange={(v) => update('pin_code', v.replace(/\D/g, ''))} tid="reg-pin" />

                    <div className="pt-2 border-t border-sdf-line">
                        <div className="text-xs uppercase font-bold tracking-widest text-sdf-text-2">Optional</div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="GSTIN" value={form.gstin} onChange={(v) => update('gstin', v.toUpperCase())} tid="reg-gstin" />
                        <Field label="PAN" value={form.pan} onChange={(v) => update('pan', v.toUpperCase())} tid="reg-pan" />
                    </div>
                    <Field label="Email Address" type="email" value={form.email} onChange={(v) => update('email', v)} tid="reg-email" />

                    {error && <div className="text-sm text-red-700 rounded bg-red-50 border border-red-200 px-3 py-2">{error}</div>}
                    <button onClick={submit} disabled={busy} className="btn-red-lg" data-testid="reg-submit">{busy ? 'Submitting…' : 'Submit Registration'}</button>
                    <div className="text-xs text-center text-sdf-text-2">
                        Already registered? <Link to="/login" className="text-sdf-red font-bold">Sign In</Link>
                    </div>
                </div>
            </main>
        </div>
    );
}

function Field({ label, required, textarea, prefix, type = 'text', value, onChange, maxLength, tid }) {
    return (
        <div>
            <label className="text-xs font-bold text-sdf-text-2 tracking-wider">{label.toUpperCase()}{required && <span className="text-sdf-red ml-0.5">*</span>}</label>
            <div className="mt-1.5 flex items-start border border-sdf-line rounded-xl overflow-hidden bg-white">
                {prefix && <span className="px-3 py-3 text-sdf-text-2 border-r border-sdf-line">{prefix}</span>}
                {textarea ? (
                    <textarea rows={3} value={value} onChange={(e) => onChange(e.target.value)} className="flex-1 px-3 py-3 outline-none text-sm bg-transparent resize-none" data-testid={tid} />
                ) : (
                    <input type={type} value={value} maxLength={maxLength} onChange={(e) => onChange(e.target.value)} className="flex-1 px-3 py-3 outline-none text-sm bg-transparent" data-testid={tid} />
                )}
            </div>
        </div>
    );
}
